<template>
  <component
    :is="embedded ? 'div' : 'a'"
    :class="['sensor-feed-silo', { 'silo-card-button': !embedded, 'sensor-feed-silo--compact': compact }]"
    @click="onWrapperClick"
  >
    <!-- Loading placeholder -->
    <div v-if="loading" class="silo-loading">
      <AnimadePlaceHolder :height="compact ? '280px' : '400px'" width="100%" borderRadius="8px"/>
    </div>
    
    <!-- Error message -->
    <div v-else-if="error" class="silo-error">
      <div class="error-content">
        <p class="error-title">Erro ao carregar silo</p>
        <p class="error-message">{{ siloData.silo_name }}</p>
      </div>
    </div>
    
    <!-- SiloCard con datos reales -->
    <SiloCard v-else-if="siloData" :silo="transformedSiloData" :plate-height="plateHeight" :compact="compact" :farm-pk="farmPk" />
  </component>
</template>

<script>
import SiloCard from './SiloCard.vue';
import AnimadePlaceHolder from './AnimadePlaceHolder.vue';
import { apiRoutes } from "../utils/api";
import { format, subDays } from 'date-fns';
import {
  applyStableProfileMetrics,
  getLastStableSiloProfile,
  getStabilityFilterThreshold,
} from '../utils/siloProfileStabilityFilter';
import {
  findFoodByPk,
  getPlateHeightFromSettings,
  getSiloDisplayWeightAndBags,
  transformSiloProfileForCard,
} from '../utils/transformSiloProfileForCard';
import {
  findSiloDimensionsCurve,
  hasSiloDimensionsCurve,
  mergeProfileWithDimensionsCurve,
  normalizeSiloLastProfile,
} from '../utils/siloLevelToKg';

export default {
  name: 'SensorFeedSiloHome',
  components: {
    SiloCard,
    AnimadePlaceHolder
  },
  props: {
    sensor: {
      type: Object,
      required: true
    },
    barn: {
      type: Object,
      required: true
    },
    batch: {
      type: Array,
      default: () => []
    },
    sensors: {
      type: Array,
      default: () => []
    },
    embedded: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false,
    },
    farmPk: {
      type: Number,
      default: null,
    }
  },
  data() {
    return {
      siloData: null,
      siloSettings: [],
      loading: true,
      error: false,
      loadGeneration: 0,
    }
  },
  computed: {
    companySettings() {
      return this.$store.getters.getCompanySettings;
    },
    company() {
      return this.$store.getters.getTargetCompany;
    },
    sacaWeight() {
      return this.companySettings?.settings?.client_frontend?.weight_of_feed_bag ?? 60;
    },
    foodsByCompany() {
      const foods = this.$store.getters.getFoodsByCompany(this.company);
      // Asegurar que siempre sea un array
      return Array.isArray(foods) ? foods : [];
    },
    plateHeight() {
      return getPlateHeightFromSettings(this.siloSettings);
    },
    transformedSiloData() {
      return transformSiloProfileForCard(this.siloData, {
        sensorId: this.sensor.pk,
        siloPk: this.sensor?.silo,
        sacaWeight: this.sacaWeight,
        foodByCompany: this.foodsByCompany,
        siloNameFallback: this.sensor?.silo_name || this.sensor?.name,
        siloSettings: this.siloSettings,
      });
    }
  },
  methods: {
    buildPriorityFeedData(sensorQuery, siloQuery) {
      const fallbackName = this.sensor?.name || this.sensor?.silo_name || `Silo ${siloQuery}`
      const safeSilo = siloQuery ?? this.sensor?.silo ?? null
      const safeSensor = sensorQuery ?? this.sensor?.pk ?? this.sensor?.sensor ?? null

      if (this.siloData) {
        return {
          ...this.siloData,
          silo: this.siloData?.silo ?? safeSilo,
          sensor: this.siloData?.sensor ?? safeSensor,
          silo_name: this.siloData?.silo_name || fallbackName,
          name: this.siloData?.name || this.siloData?.silo_name || fallbackName
        }
      }

      // Placeholder mínimo para que a tela de detalhe priorize este silo
      // mesmo quando o restante da Home ainda está carregando.
      return {
        sensor: safeSensor,
        silo: safeSilo,
        silo_name: fallbackName,
        name: fallbackName,
        level: 0,
        weight: 0,
        datetime_fetched: null
      }
    },
    onWrapperClick() {
      if (!this.embedded) {
        this.changeBarnTarget();
      }
    },

    changeBarnTarget() {
      this.$store.commit("setBarnTarget", { barn: this.barn, farmPk: this.farmPk })
      this.$store.commit("setBatchTarget", this.batch[0] || null)
      const siloQuery = this.sensor?.silo?.pk ?? this.sensor?.silo?.id ?? this.sensor?.silo
      const sensorQuery = this.sensor?.pk ?? this.sensor?.sensor ?? this.sensor?.id ?? null
      const priorityData = this.buildPriorityFeedData(sensorQuery, siloQuery)

      this.$store.commit('setSensorsListFeed', {
        barn: this.barn,
        batch: this.batch[0] || null,
        sensor: this.sensors,
        data: [priorityData]
      })

      this.$router.push({
        path: '/batch-details/smart-feed-v3',
        query: {
          silo: siloQuery,
          sensor: sensorQuery
        }
      })
    },
    
    findFoodByPk(pk) {
      return findFoodByPk(pk, this.foodsByCompany);
    },

    emitSiloTotals() {
      const transformed = this.transformedSiloData;
      const { weight, bags } = getSiloDisplayWeightAndBags(transformed);
      const food = transformed?.food && transformed.food !== 'N/A' ? transformed.food : null;
      this.$emit('silo-totals', {
        sensorPk: this.sensor.pk,
        weight,
        bags,
        food,
      });
    },

    profileMetricsDiffer(left, right) {
      if (!left || !right) return left !== right;
      return left.level !== right.level
        || left.weight !== right.weight
        || left.volume !== right.volume
        || left.food !== right.food
        || left.datetime_fetched !== right.datetime_fetched;
    },

    isLoadCurrent(generation) {
      return generation === this.loadGeneration;
    },

    showProfileImmediately(profile, siloPk) {
      this.siloData = profile;
      this.loading = false;
      this.error = false;
      this.persistSiloProfile(siloPk, profile);
    },

    applyRefinedProfile(rawProfile, refinedProfile, siloPk, generation) {
      if (!this.isLoadCurrent(generation) || !refinedProfile) return;
      if (!this.profileMetricsDiffer(rawProfile, refinedProfile)) return;
      this.siloData = refinedProfile;
      this.persistSiloProfile(siloPk, refinedProfile);
    },

    async applySiloSettingsFromResponse(settingsResponse) {
      try {
        if (!settingsResponse?.ok) {
          this.siloSettings = [];
          return;
        }
        const settingsData = await settingsResponse.json();
        this.siloSettings = settingsData.results || [];
      } catch (err) {
        console.error('Could not parse silo settings.', err);
        this.siloSettings = [];
      }
    },

    enrichProfileWithDimensions(profile, siloPk) {
      if (!profile || hasSiloDimensionsCurve(profile.curves)) {
        return profile;
      }

      const cachedData = this.$store.getters.getCachedSiloDataHome(this.barn.pk, siloPk, 60);
      const cachedCurve = findSiloDimensionsCurve(cachedData?.curves);
      if (cachedCurve) {
        return mergeProfileWithDimensionsCurve(profile, cachedCurve);
      }

      return profile;
    },

    async fetchSiloSettingsOnly(siloPk) {
      try {
        const response = await fetch(apiRoutes.siloSettings(siloPk), {
          method: 'GET',
          headers: this.$store.getters.getHeader,
        });
        await this.applySiloSettingsFromResponse(response);
      } catch (err) {
        console.error('Could not get silo settings.', err);
        this.siloSettings = [];
      }
    },

    async resolveStableDisplayProfile(rawProfile, siloSettings) {
      if (!rawProfile) return null;

      const sensorPk = this.sensor?.pk ?? this.sensor?.sensor;
      if (!sensorPk) return rawProfile;

      const threshold = getStabilityFilterThreshold(siloSettings);
      const startDate = format(subDays(new Date(), 3), "yyyy-MM-dd'T'HH:mm:ss");
      const endDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");

      try {
        const historyResult = await this.$store.dispatch('fetchSiloProfileHistory', {
          sensor_pk: sensorPk,
          start_date: startDate,
          end_date: endDate,
        });
        const historyProfiles = Array.isArray(historyResult)
          ? historyResult
          : (historyResult?.results ?? []);
        const stableProfile = getLastStableSiloProfile(historyProfiles, threshold);
        return applyStableProfileMetrics(rawProfile, stableProfile);
      } catch (err) {
        console.warn('Could not apply silo stability filter on home:', err);
        return rawProfile;
      }
    },

    async refineProfileInBackground(rawProfile, siloPk, generation, headers, { settingsLoaded = false } = {}) {
      if (!rawProfile || !this.isLoadCurrent(generation)) return;

      try {
        if (!settingsLoaded) {
          const settingsResponse = await fetch(apiRoutes.siloSettings(siloPk), {
            method: 'GET',
            headers,
          });
          if (!this.isLoadCurrent(generation)) return;
          await this.applySiloSettingsFromResponse(settingsResponse);
        }

        const enrichedProfile = this.enrichProfileWithDimensions(rawProfile, siloPk);
        if (this.profileMetricsDiffer(rawProfile, enrichedProfile)) {
          this.siloData = enrichedProfile;
          this.persistSiloProfile(siloPk, enrichedProfile);
        }

        const refinedProfile = await this.resolveStableDisplayProfile(enrichedProfile, this.siloSettings);
        this.applyRefinedProfile(enrichedProfile, refinedProfile, siloPk, generation);
      } catch (err) {
        console.warn('Could not refine silo profile on home:', err);
      }
    },

    persistSiloProfile(siloPk, displayProfile) {
      this.$store.commit('setSensorsListFeed', {
        barn: this.barn,
        batch: this.batch[0],
        sensor: this.sensors,
        data: [displayProfile]
      });

      this.$store.commit('setSiloDataHomeByBarn', {
        barnPk: this.barn.pk,
        siloPk,
        data: displayProfile,
        sensors: this.sensors,
        lastUpdated: new Date().toISOString()
      });
    },

    async loadSiloData() {
      const generation = ++this.loadGeneration;
      const siloPk = this.sensor?.silo;

      if (!siloPk) {
        this.loading = false;
        return;
      }

      const cachedData = this.$store.getters.getCachedSiloDataHome(
        this.barn.pk,
        siloPk,
        5
      );

      if (cachedData) {
        void this.fetchSiloSettingsOnly(siloPk);
        this.showProfileImmediately(
          this.enrichProfileWithDimensions(normalizeSiloLastProfile(cachedData), siloPk),
          siloPk
        );
        this.refreshSiloDataInBackground(generation);
        return;
      }

      await this.fetchSiloLastProfile(generation);
    },

    refreshSiloDataInBackground(generation) {
      void this.fetchSiloLastProfile(generation, { background: true });
    },

    async fetchSiloLastProfile(generation = ++this.loadGeneration, { background = false } = {}) {
      const siloPk = this.sensor?.silo;
      if (!siloPk) {
        this.loading = false;
        return;
      }

      if (!background) {
        this.loading = true;
        this.error = false;
      }

      const headers = this.$store.getters.getHeader;
      const profileUrl = apiRoutes.siloLastProfileBySilo(siloPk, undefined, undefined, true);
      const settingsUrl = apiRoutes.siloSettings(siloPk);

      try {
        const [profileResponse, settingsResponse] = await Promise.all([
          fetch(profileUrl, { headers }),
          fetch(settingsUrl, { method: 'GET', headers }),
        ]);
        if (!this.isLoadCurrent(generation)) return;

        await this.applySiloSettingsFromResponse(settingsResponse);

        if (!profileResponse.ok) {
          throw new Error(`HTTP error! status: ${profileResponse.status}`);
        }

        const data = this.enrichProfileWithDimensions(
          normalizeSiloLastProfile(await profileResponse.json()),
          siloPk
        );

        if (background && this.siloData && !this.profileMetricsDiffer(this.siloData, data)) {
          void this.refineProfileInBackground(data, siloPk, generation, headers, { settingsLoaded: true });
          return;
        }

        this.showProfileImmediately(data, siloPk);
        void this.refineProfileInBackground(data, siloPk, generation, headers, { settingsLoaded: true });
      } catch (err) {
        if (!this.isLoadCurrent(generation)) return;
        console.error('Error fetching silo data:', err);
        if (!background || !this.siloData) {
          this.error = true;
          this.siloData = null;
          this.siloSettings = [];
        }
      } finally {
        if (this.isLoadCurrent(generation) && !background) {
          this.loading = false;
        }
      }
    }
  },
  created() {
    void this.loadSiloData();
  },
  watch: {
    // Re-cargar (con cache) si cambia el sensor
    'sensor.silo': {
      handler() {
        this.loadSiloData();
      }
    },
    // Notificar al padre (BarnSilos) peso y sacas para el resumen total
    transformedSiloData: {
      handler() {
        this.emitSiloTotals();
      },
      immediate: true,
      deep: true,
    },
    foodsByCompany: {
      handler() {
        this.emitSiloTotals();
      },
    },
  }
};
</script>

<style scoped>
.sensor-feed-silo {
  height: 100%;
  display: flex;
}

.sensor-feed-silo--compact {
  width: 100%;
  max-width: 200px;
}

.silo-card-button {
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: block;
}

.silo-card-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.silo-card-button:active {
  transform: translateY(0);
}

.silo-loading {
  padding: 8px;
  width: 100%;
}

.silo-error {
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  width: 100%;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.error-title {
  font-size: 16px;
  font-weight: 600;
  color: #991b1b;
  margin: 0;
}

.error-message {
  font-size: 14px;
  color: #b91c1c;
  margin: 0;
}
</style>